import { digest, hasRecoveryFile, publicJwk } from "../../../../../supabase/functions/sync-control/policy";
import { base64url, canonical, newDataKey, sealDeviceEnvelope, unbase64url, unwrapDataKeyBytes } from "./cryptoV2";
import { rotateKeyHistory } from "./keyHistory";
import type { RecoveryDraft } from "./recoveryDraft";
import type { ManagementContext } from "./syncManagement";
import { dataKeyScope } from "./syncScopes";

export async function disconnectDevice(context: ManagementContext, revokedId: string, draft?: RecoveryDraft) {
  const { api, device, engine } = context;
  const summary = await api.devices();
  const root = summary.root;
  if (!root || !engine) throw new Error("sync-checkpoint-not-ready");
  if (revokedId === device.deviceId) throw new Error("sync-cannot-revoke-current");
  if (!summary.devices.some((d) => d.id === revokedId && !d.revokedAt)) throw new Error("sync-control-race");
  if (canonical(dataKeyScope(root)) !== canonical(device.scope)) throw new Error("sync-key-version-required");
  const scope = { ...dataKeyScope(root), keyVersion: root.keyVersion + 1 };
  const deviceOnly = !hasRecoveryFile(root, summary.devices);
  const verified = !deviceOnly ? draft?.verifiedMaterial() : undefined;
  if (!deviceOnly && (!verified || canonical(dataKeyScope(verified)) !== canonical(scope)))
    throw new Error("sync-recovery-file-verify-required");
  const material = verified?.dataKey ?? newDataKey();
  const key = await crypto.subtle.importKey("raw", unbase64url(material, 32), "AES-GCM", false, ["encrypt", "decrypt"]);
  const bytes = await unwrapDataKeyBytes(device.wrapping, device.scope, device.wrapped);
  let keyHistory;
  try {
    keyHistory = await rotateKeyHistory(device.scope, device.dataKey, root.keyHistory, base64url(bytes), material);
  } finally {
    bytes.fill(0);
  }
  const deviceEnvelopes: Record<string, unknown> = {};
  for (const recipient of summary.devices.filter((d) => !d.revokedAt && d.id !== revokedId))
    deviceEnvelopes[recipient.id] = await sealDeviceEnvelope(
      recipient.agreement,
      { ...scope, objectId: recipient.id },
      { dataKey: material },
    );
  await engine.checkpoint(
    async (checkpoint) => {
      const target = {
        root: {
          datasetId: scope.datasetId,
          generation: scope.generation,
          keyVersion: scope.keyVersion,
          recoveryPublicKey: verified ? publicJwk(draft!.publicKey) : root.recoveryPublicKey,
          ...(deviceOnly ? { recoveryMode: "device" as const } : {}),
          deviceEnvelopes,
          keyHistory,
          checkpoint,
        },
        expectedRootHash: await digest(root),
        revokedDeviceId: revokedId,
        revokedSequence: checkpoint.clock[revokedId] ?? 0,
      };
      // Do not replay an uncertain mutation. Read back the exact committed root;
      // a later sync can adopt its own envelope after a lost reply or reload.
      try {
        await api.request("device-disconnect", {
          ...target,
          ...(verified ? { recoverySignature: await draft!.sign({ accountId: device.accountId, ...target }) } : {}),
        });
      } catch (error) {
        const after = await api.devices();
        if (
          !after.devices.find((d) => d.id === revokedId)?.revokedAt ||
          !after.root ||
          canonical(dataKeyScope(after.root)) !== canonical(scope) ||
          canonical(after.root.deviceEnvelopes[device.deviceId]) !== canonical(deviceEnvelopes[device.deviceId])
        )
          throw error;
      }
    },
    true,
    { version: scope.keyVersion, key },
  );
}
